/**
 * Secure role-based authorization helpers
 * Uses the user_roles table with SECURITY DEFINER function for protection
 */

export type AppRole = 'admin' | 'landlord' | 'renter';

// Define a flexible profile type that works with any profile object
export interface ProfileWithRoles {
  roles?: AppRole[];
  user_type?: 'renter' | 'landlord'; // Legacy field for fallback
  [key: string]: any;
}

/**
 * Check if a user has a specific role
 * Uses the secure roles array fetched from user_roles table
 * Falls back to empty array if roles not loaded yet
 */
export const hasRole = (profile: ProfileWithRoles | null | undefined, role: AppRole): boolean => {
  if (!profile) return false;
  // Use the secure roles array from user_roles table
  return profile.roles?.includes(role) || false;
};

/**
 * Check if user is a landlord
 */
export const isLandlord = (profile: ProfileWithRoles | null | undefined): boolean => {
  return hasRole(profile, 'landlord');
};

/**
 * Check if user is a renter
 */
export const isRenter = (profile: ProfileWithRoles | null | undefined): boolean => {
  return hasRole(profile, 'renter');
};

/**
 * Check if user is an admin
 */
export const isAdmin = (profile: ProfileWithRoles | null | undefined): boolean => {
  return hasRole(profile, 'admin');
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (profile: ProfileWithRoles | null | undefined, roles: AppRole[]): boolean => {
  if (!profile) return false;
  return roles.some(role => hasRole(profile, role));
};

/**
 * Check if user has all of the specified roles
 */
export const hasAllRoles = (profile: ProfileWithRoles | null | undefined, roles: AppRole[]): boolean => {
  if (!profile) return false;
  return roles.every(role => hasRole(profile, role));
};

/**
 * Get the primary role for display purposes
 * Priority: admin > landlord > renter
 * Falls back to legacy user_type for redirect purposes only when roles array is empty
 */
export const getPrimaryRole = (profile: ProfileWithRoles | null | undefined): AppRole | null => {
  if (!profile) return null;
  
  if (hasRole(profile, 'admin')) return 'admin';
  if (hasRole(profile, 'landlord')) return 'landlord';
  if (hasRole(profile, 'renter')) return 'renter';
  
  // Fallback to legacy user_type for display/redirect purposes only (not for authorization)
  if (!profile.roles?.length && profile.user_type) {
    return profile.user_type === 'landlord' ? 'landlord' : 'renter';
  }
  
  return null;
};
